import React, { useState } from 'react';
import styled from 'styled-components';
import { TaskData } from '../../../api/endpoints/tasks';
import { Icon } from '../../../concepts/resources/Icon';
import { FauxCopy } from '../../../concepts/text/FauxCopy';
import { Button, Colors, Float, remInPx, usePrompt } from '../../../ui';
import { Position } from '../../../ui/Position';
import { TableButton } from '../../../ui/TableButton';
import { QuillInput } from '../../packages/mentions/QuillInput';
import { Flexer } from '../elements/LayoutElements';
import { useOnPatchTask } from './TaskActions';

const Wrapper = styled.div`
    position: relative;
`;

const InputFrame = styled.div`
    width: 100%;
    font-size: 0.9375rem;
    font-weight: 400;
    color: ${Colors.text};
    border: solid .0625rem ${Colors.border};
    border-radius: .375rem;
    padding: .1875rem .375rem;
`;

const External = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: 100%;
    top: 50%;
    opacity: 0;
    transform: translate(0%, -50%);
    transition: opacity .1s ease-in-out, transform .1s ease-in-out;

    ${TableButton}:hover & {
        opacity: 1;
        transform: translate(25%, -50%);
    }
`;

const SummaryEditor = ({ initialValue, onComplete }: { initialValue: string, onComplete: (nextValue: string) => void }) => {

    const [value, setValue] = useState(initialValue?.trim() || '');

    const promptRef = usePrompt({
        onAction(confirm) {
            if (confirm) {
                onComplete(value);
            }
            else {
                onComplete(null);
            }
        }
    });

    return <InputFrame ref={promptRef}>
        <QuillInput
            initialValue={value}
            onChange={(nextValue: string) => setValue(nextValue)}
            formatting={['mention', 'bold', 'italic']}
            textSettings={{
                fontSize: remInPx(.9375),
                lineHeight: 1.5,
            }}
            autoFocus
        />
        <Position at="bottomLeft">
            <Flexer relative padding=".25" transform="translateY(100%)" zIndex={100}>
                <Float shadow="xl">
                    <Flexer relative flex="row" center gap=".25" padding=".25">
                        <Button variant="positive" size="sm" onAction={() => onComplete(value)}>
                            <Icon size="1" src="deqo://icons/hero/check" />
                        </Button>
                        <Button variant="negative" size="sm" onAction={() => onComplete(null)}>
                            <Icon size="1" src="deqo://icons/hero/x" />
                        </Button>
                    </Flexer>
                </Float>
            </Flexer>
        </Position>
    </InputFrame>;
};

export const TaskSummaryCell = React.memo(({ readonly, task }: { readonly?: boolean, task: TaskData }) => {

    const { summary, status } = (task);
    const done = status === 'done' || status === 'closed';

    const onPatchTask = useOnPatchTask(task.id);

    const [editing, setEditing] = useState(false);
    const onDoneEditing = (nextValue: string) => {
        setEditing(false);
        if (!!nextValue?.trim()) {
            onPatchTask({ summary: nextValue });
        }
    };

    return <Flexer minWidth="18" style={{ whiteSpace: 'normal' }}>
        {!editing && <Wrapper>
            <TableButton disabled={readonly} onClick={() => setEditing(true)} >
                <FauxCopy
                    align="left"
                    size="0.9375"
                    wordBreak="break-word"
                    flush
                    strike={done}
                    color={done ? Colors.textLightest : Colors.text}
                    value={summary?.trim() || 'No summary'}
                />
                <External>
                    <Icon size="1" src="deqo://icons/hero/pencil" />
                </External>
            </TableButton>
        </Wrapper>}
        {!!editing && <Wrapper>
            <SummaryEditor initialValue={summary} onComplete={onDoneEditing} />
        </Wrapper>}
    </Flexer>;

});
